'use client'

import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useRouter } from 'next/navigation'
import EditAccount from '@/components/otherPages/EditAccount'
import { loginWithCredentials } from '@/utils/auth'

const API_REQUEST_OTP = '/api/member/register/register_requestOtp'
const API_VERIFY_OTP = '/api/member/register/register_verifyOtp'
const API_ADD_MEMBER = '/api/member/register/add_Member'
const API_GET_OFFLINE_STORES = '/api/offline/get_OfflineStoreList'
const API_GET_BY_MOBILE = '/api/member/get_MemberbyMobliePhone'
const API_ADD_POINTS = '/api/member/add_RegisteredMemberPoints'
const API_GET_SIGNUP_POINTS = '/api/member/get_SignupBonusPoints'
const API_GET_STORE_INFO = '/api/online/get_StoreInfo'
const API_GET_PARTNER_STORES = '/api/member/get_PartnerStoreList'
const API_GET_STORE_REFERRERS = '/api/member/get_StoreReferrers'

const defaultBirthday = '2000-01-01'
const OTP_LENGTH = 6

const pad2 = (value) => String(value).padStart(2, '0')
const daysInMonth = (year, month) => new Date(year, month, 0).getDate()
const toFlag = (value) => value === true || value === 1 || value === '1'
const unwrapData = (payload) => payload?.Data ?? payload ?? {}
const normalizeOfflineStoreId = (value) => {
  const text = String(value ?? '').trim()
  if (!text || text === "'") return ''
  return text
}

const isValidPassword = (pw) =>
  typeof pw === 'string' &&
  pw.length >= 8 &&
  pw.length <= 20 &&
  /[A-Za-z]/.test(pw) &&
  /\d/.test(pw)

const isValidOptionalEmail = (value) => {
  const text = String(value || '').trim()
  if (!text) return true
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)
}

const isValidMemberName = (value) => {
  const text = String(value || '').trim()
  if (!text) return false
  return /^[\p{L}\s]+$/u.test(text)
}

const normalizeOtp = (value) =>
  String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, OTP_LENGTH)

const formatReferrerLabel = (item) => {
  const empId = String(item?.EmpID ?? '').trim()
  const empName = String(item?.EmpName ?? '').trim()

  if (!empId && !empName) return ''
  if (!empName || empName === empId) return empId
  return `${empName} (${empId})`
}

// GTM / GA4
const pushDataLayerEvent = (eventName, ecommerce = null, extra = {}) => {
  if (typeof window === 'undefined') return

  window.dataLayer = window.dataLayer || []

  if (ecommerce) {
    window.dataLayer.push({ ecommerce: null })
  }

  window.dataLayer.push({
    event: eventName,
    ...(ecommerce ? { ecommerce } : {}),
    ...extra,
  })
}

export function RegisterWizard({
  defaultMobile = '',
  lineId = '',
  redirectTo = '',
}) {
  const router = useRouter()
  const isLineMode = !!String(lineId || '').trim()
  const isMobileLocked = !!String(defaultMobile || '').trim()
  const storeNumber = String(process.env.NEXT_PUBLIC_STORE_NUMBER || '').trim()

  const [step, setStep] = useState(1)
  const [mobile, setMobile] = useState(defaultMobile || '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [birthday, setBirthday] = useState(defaultBirthday)
  const [birthYear, setBirthYear] = useState(2000)
  const [birthMonth, setBirthMonth] = useState(1)
  const [birthDay, setBirthDay] = useState(1)
  const [memberName, setMemberName] = useState('')
  const [email, setEmail] = useState('')
  const [gender, setGender] = useState('')

  const [stores, setStores] = useState([])
  const [selectedStoreId, setSelectedStoreId] = useState('')
  const [loadingStores, setLoadingStores] = useState(false)

  const [hasFranchise, setHasFranchise] = useState(false)
  const [storeMode, setStoreMode] = useState('offline')
  const [defaultOfflineStoreId, setDefaultOfflineStoreId] = useState('')
  const [partnerStores, setPartnerStores] = useState([])
  const [selectedPartnerStoreId, setSelectedPartnerStoreId] = useState('')
  const [loadingPartnerStores, setLoadingPartnerStores] = useState(false)

  const [isReferralRequired, setIsReferralRequired] = useState(false)
  const [referrers, setReferrers] = useState([])
  const [selectedReferrerId, setSelectedReferrerId] = useState('')
  const [loadingReferrers, setLoadingReferrers] = useState(false)

  const [sessionId, setSessionId] = useState(null)
  const [otp, setOtp] = useState('')
  const [cooldown, setCooldown] = useState(0)
  const [loading, setLoading] = useState(false)

  // Google ADS
  const [adsId, setAdsId] = useState(null)
  const [labelMemberSuccess, setLabelMemberSuccess] = useState(null)

  const acRef = useRef(null)

  useEffect(() => () => acRef.current?.abort(), [])

  const newAbortController = () => {
    acRef.current?.abort()
    acRef.current = new AbortController()
    return acRef.current
  }

  const getJson = async (url) => {
    const controller = newAbortController()
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || data?.Error) {
      throw new Error(data?.Error || data?.error || `HTTP ${response.status}`)
    }
    return data
  }

  const postJson = async (url, body) => {
    const controller = newAbortController()
    const response = await fetch(url, {
      method: 'POST',
      cache: 'no-store',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || data?.Error) {
      throw new Error(data?.Error || data?.error || `HTTP ${response.status}`)
    }
    return data
  }

  const formatOccDate8 = (date = new Date()) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}${month}${day}`
  }

  useEffect(() => {
    const [year, month, day] = (birthday || defaultBirthday)
      .split('-')
      .map(Number)

    setBirthYear(year || 2000)
    setBirthMonth(month || 1)
    setBirthDay(day || 1)
  }, [birthday])

  useEffect(() => {
    if (!birthYear || !birthMonth || !birthDay) return

    const maxDay = daysInMonth(birthYear, birthMonth)
    const safeDay = Math.min(birthDay, maxDay)

    if (safeDay !== birthDay) {
      setBirthDay(safeDay)
      return
    }

    setBirthday(`${birthYear}-${pad2(birthMonth)}-${pad2(safeDay)}`)
  }, [birthYear, birthMonth, birthDay])

  useEffect(() => {
    if (!hasFranchise) {
      setStoreMode('offline')
    }
  }, [hasFranchise])

  useEffect(() => {
    const fetchPartnerStores = async () => {
      if (!isLineMode || !hasFranchise || !storeNumber) {
        setPartnerStores([])
        setSelectedPartnerStoreId('')
        setLoadingPartnerStores(false)
        return
      }

      setLoadingPartnerStores(true)

      try {
        const query = new URLSearchParams({ StoreNumber: storeNumber })
        const payload = await getJson(
          `${API_GET_PARTNER_STORES}?${query.toString()}`
        )
        const rawList = payload?.Data
        const list = Array.isArray(rawList) ? rawList : []

        setPartnerStores(list)
        setSelectedPartnerStoreId((current) => {
          const exists =
            current &&
            list.some((item) => String(item?.PartnerStoreID ?? '') === current)
          return exists ? current : String(list[0]?.PartnerStoreID ?? '')
        })
      } catch (error) {
        console.warn('fetch partner stores failed:', error)
        setPartnerStores([])
        setSelectedPartnerStoreId('')
        toast.error('無法取得加盟店資料，請稍後再試')
      } finally {
        setLoadingPartnerStores(false)
      }
    }

    fetchPartnerStores()
  }, [hasFranchise, isLineMode, storeNumber])

  useEffect(() => {
    const fetchStoreSetup = async () => {
      setLoadingStores(true)

      try {
        let resolvedDefaultOfflineStoreId = ''

        if (storeNumber) {
          try {
            const query = new URLSearchParams({ StoreNumber: storeNumber })
            const storeInfoResponse = await getJson(
              `${API_GET_STORE_INFO}?${query.toString()}`
            )
            const storeInfo = unwrapData(storeInfoResponse)

            setAdsId(storeInfo?.GoogleAdsID)
            setLabelMemberSuccess(storeInfo?.LabelMemberSuccess)

            setHasFranchise(toFlag(storeInfo?.HasFranchise))
            setIsReferralRequired(toFlag(storeInfo?.IsReferralRequired))
            resolvedDefaultOfflineStoreId = normalizeOfflineStoreId(
              storeInfo?.DefaultOfflineStoreID
            )
            setDefaultOfflineStoreId(resolvedDefaultOfflineStoreId)
          } catch (error) {
            console.warn('fetch store info failed:', error)
            setHasFranchise(false)
            setIsReferralRequired(false)
            setDefaultOfflineStoreId('')
          }
        }

        const url = `${API_GET_OFFLINE_STORES}?StoreNumber=${encodeURIComponent(
          storeNumber
        )}`
        const response = await fetch(url, { cache: 'no-store' })
        const data = await response.json().catch(() => ({}))
        const list = data?.Data?.StoreData || []

        setStores(list)

        if (list.length === 1) {
          setSelectedStoreId(String(list[0]?.OfflineStoreID ?? ''))
        } else if (list.length > 1) {
          setSelectedStoreId((current) => {
            const exists =
              current &&
              list.some(
                (item) => String(item?.OfflineStoreID ?? '') === String(current)
              )
            return exists ? current : ''
          })
        } else {
          setSelectedStoreId('')
        }
      } catch (error) {
        console.warn('fetch stores failed:', error)
        toast.error('無法取得註冊店櫃，請稍後再試')
      } finally {
        setLoadingStores(false)
      }
    }

    fetchStoreSetup()
  }, [isLineMode, storeNumber])

  const usingFranchise = hasFranchise && storeMode === 'franchise'
  const shouldShowOfflineStoreSelect =
    isLineMode && !usingFranchise
  const shouldRequireRegistrationStore =
    !usingFranchise &&
    (isLineMode || !!defaultOfflineStoreId)
  const effectiveStoreId = usingFranchise
    ? ''
    : selectedStoreId || defaultOfflineStoreId || ''
  const effectivePartnerStoreId = usingFranchise ? selectedPartnerStoreId : ''
  const hasSelectedRegistrationStore = usingFranchise
    ? !!effectivePartnerStoreId
    : !shouldRequireRegistrationStore || !!effectiveStoreId
  const shouldShowReferrer =
    isLineMode && !usingFranchise && isReferralRequired && !!selectedStoreId

  useEffect(() => {
    const fetchReferrers = async () => {
      if (!shouldShowReferrer) {
        setReferrers([])
        setSelectedReferrerId('')
        setLoadingReferrers(false)
        return
      }

      setLoadingReferrers(true)

      try {
        const query = new URLSearchParams({
          StoreNumber: storeNumber,
          OfflineStoreID: selectedStoreId,
        })
        const payload = await getJson(
          `${API_GET_STORE_REFERRERS}?${query.toString()}`
        )
        const rawList = payload?.Data
        const list = Array.isArray(rawList) ? rawList : []

        setReferrers(list)
        setSelectedReferrerId(
          list.length > 0 ? String(list[0]?.EmpID ?? '') : ''
        )
      } catch (error) {
        console.warn('fetch referrers failed:', error)
        setReferrers([])
        setSelectedReferrerId('')
        toast.error('無法取得推薦人資料，請稍後再試')
      } finally {
        setLoadingReferrers(false)
      }
    }

    fetchReferrers()
  }, [selectedStoreId, shouldShowReferrer, storeNumber])

  useEffect(() => {
    if (cooldown <= 0) return

    const timer = setInterval(() => {
      setCooldown((current) => (current > 0 ? current - 1 : 0))
    }, 1000)

    return () => clearInterval(timer)
  }, [cooldown])

  const handleRequestOtp = async () => {
    if (!mobile.trim()) {
      toast.error('請輸入手機')
      return
    }

    if (!memberName.trim()) {
      toast.error('請輸入姓名')
      return
    }

    if (!isValidMemberName(memberName)) {
      toast.error('姓名不可包含特殊符號')
      return
    }

    if (!gender) {
      toast.error('請選擇性別')
      return
    }

    if (!isValidOptionalEmail(email)) {
      toast.error('請輸入正確的電子信箱格式')
      return
    }

    if (!hasSelectedRegistrationStore) {
      toast.error(usingFranchise ? '請選擇加盟店' : '請選擇註冊店櫃')
      return
    }

    if (shouldShowReferrer && !selectedReferrerId) {
      toast.error('請選擇推薦人')
      return
    }

    if (!isLineMode) {
      if (!password || !confirmPassword) {
        toast.error('請輸入密碼與再次輸入密碼')
        return
      }

      if (password !== confirmPassword) {
        toast.error('兩次輸入的密碼不一致')
        return
      }

      if (!isValidPassword(password)) {
        toast.error('密碼需為 8 到 20 碼，且同時包含英文與數字')
        return
      }
    }

    setLoading(true)

    try {
      const data = await postJson(API_REQUEST_OTP, {
        StoreNumber: process.env.NEXT_PUBLIC_STORE_NUMBER,
        Channel: 'sms',
        Mobile: mobile.trim(),
        LineID: isLineMode ? lineId : null,
      })

      setSessionId(data.SessionID)
      setStep(2)
      setCooldown(60)
      toast.success('驗證碼已送出')
    } catch (error) {
      toast.error(`取得驗證碼失敗：${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (cooldown > 0) return
    await handleRequestOtp()
  }

  const handleVerifyAndCreate = async () => {
    if (!sessionId) {
      toast.error('請先取得驗證碼')
      return
    }

    const normalizedOtp = normalizeOtp(otp)
    if (normalizedOtp.length !== OTP_LENGTH) {
      toast.error('請輸入 6 位數驗證碼')
      return
    }

    if (!isValidMemberName(memberName)) {
      toast.error('姓名不可包含特殊符號')
      return
    }

    setLoading(true)

    try {
      const verify = await postJson(API_VERIFY_OTP, {
        StoreNumber: process.env.NEXT_PUBLIC_STORE_NUMBER,
        SessionID: sessionId,
        Code: normalizedOtp,
      })

      const registrationToken = verify.RegistrationToken
      const passwordToSend = isLineMode ? mobile.trim() : password

      await postJson(API_ADD_MEMBER, {
        StoreNumber: Number(process.env.NEXT_PUBLIC_STORE_NUMBER),
        RegistrationToken: registrationToken,
        UserName: mobile.trim(),
        Password: passwordToSend,
        Mobile: mobile.trim(),
        Mail01: email.trim() || null,
        Gender: Number(gender),
        Birthday: birthday,
        LineID: isLineMode ? lineId : null,
        MemberName: memberName.trim(),
        OfflineStoreID: usingFranchise ? '' : effectiveStoreId || null,
        ReferrerID:
          isLineMode && shouldShowReferrer ? selectedReferrerId || null : null,
        PartnerStoreID: usingFranchise ? effectivePartnerStoreId || null : null,
      })

      // GTM / GA4 + Google Ads：會員註冊成功後送出追蹤
      if (typeof window !== 'undefined') {
        // ✅ GTM / GA4：會員註冊成功
        pushDataLayerEvent('sign_up', null, {
          method: isLineMode ? 'line' : 'phone',
        })

        // ✅ Google Ads：保留原本分開送 conversion
        if (adsId && adsId !== '') {
          if (labelMemberSuccess && labelMemberSuccess !== '') {
            window.gtag?.('event', 'conversion', {
              send_to: `AW-${adsId}/${labelMemberSuccess}`,
            })
          }
        }
      }

      try {
        await loginWithCredentials({
          account: mobile.trim(),
          password: passwordToSend,
          rememberMe: true,
          onSuccess: () => toast.success('註冊成功，已為您自動登入'),
          onError: () => toast.error('自動登入失敗，請稍後手動登入'),
        })
      } catch {}

      try {
        if (isLineMode) {
          const memberQuery = new URLSearchParams({
            StoreNumber: String(process.env.NEXT_PUBLIC_STORE_NUMBER),
            Mobile: mobile.trim(),
          })
          const memberInfo = await getJson(
            `${API_GET_BY_MOBILE}?${memberQuery.toString()}`
          )
          const memberId = memberInfo?.MemberID ?? memberInfo?.Data?.MemberID

          if (memberId) {
            let signupPoints = 0

            try {
              const pointsQuery = new URLSearchParams({
                StoreNumber: String(process.env.NEXT_PUBLIC_STORE_NUMBER),
              })
              const signupInfo = await getJson(
                `${API_GET_SIGNUP_POINTS}?${pointsQuery.toString()}`
              )
              const rawPoints =
                signupInfo?.SignupBonusPoints ??
                signupInfo?.Data?.SignupBonusPoints ??
                signupInfo?.points?.SignupBonusPoints
              const parsedPoints = Number(rawPoints)

              if (Number.isFinite(parsedPoints) && parsedPoints > 0) {
                signupPoints = parsedPoints
              }
            } catch {}

            await postJson(API_ADD_POINTS, {
              MemberID: Number(memberId),
              OccDate: formatOccDate8(),
              Point: signupPoints,
            })
          }
        }
      } catch {}

      setStep(3)

      if (redirectTo) {
        setTimeout(() => {
          window.location.href = redirectTo
        }, 600)
      }
    } catch (error) {
      toast.error(`註冊失敗：${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const currentYear = new Date().getFullYear()
  const years = Array.from(
    { length: currentYear - 1900 + 1 },
    (_, index) => 1900 + index
  )
  const months = Array.from({ length: 12 }, (_, index) => index + 1)
  const days = Array.from(
    { length: daysInMonth(birthYear, birthMonth) },
    (_, index) => index + 1
  )

  const isStepOneDisabled =
    loading ||
    !mobile.trim() ||
    !memberName.trim() ||
    !gender ||
    !hasSelectedRegistrationStore ||
    (usingFranchise && (loadingPartnerStores || !selectedPartnerStoreId)) ||
    (shouldShowReferrer && (loadingReferrers || !selectedReferrerId)) ||
    !isValidOptionalEmail(email) ||
    (!isLineMode &&
      (!password ||
        !confirmPassword ||
        password !== confirmPassword ||
        !isValidPassword(password)))

  return (
    <div className="register-form">
      {step === 1 && (
        <form
          onSubmit={(event) => event.preventDefault()}
          className="needs-validation"
        >
          <div className="form-floating mb-3">
            <input
              type="tel"
              className="form-control form-control_gray"
              style={{ fontSize: '16px' }}
              placeholder="手機 *"
              value={mobile}
              onChange={(event) => {
                if (!isMobileLocked) setMobile(event.target.value.trim())
              }}
              readOnly={isMobileLocked}
              required
            />
            <label>手機 *</label>
          </div>

          <div className="form-floating mb-3">
            <input
              type="text"
              className="form-control form-control_gray"
              style={{ fontSize: '16px' }}
              placeholder="姓名 *"
              value={memberName}
              onChange={(event) => setMemberName(event.target.value)}
              required
            />
            <label>姓名 *</label>
          </div>

          {!isLineMode && (
            <>
              <div className="form-floating mb-3">
                <input
                  type="password"
                  className="form-control form-control_gray"
                  style={{ fontSize: '16px' }}
                  placeholder="密碼 *"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={8}
                  maxLength={20}
                  required
                />
                <label>密碼 *</label>
              </div>

              <div className="form-floating mb-2">
                <input
                  type="password"
                  className="form-control form-control_gray"
                  style={{ fontSize: '16px' }}
                  placeholder="再次輸入密碼 *"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  minLength={8}
                  maxLength={20}
                  required
                />
                <label>再次輸入密碼 *</label>
              </div>

              {confirmPassword && confirmPassword !== password && (
                <small style={{ color: 'red' }}>兩次輸入的密碼不一致</small>
              )}
              {password && !isValidPassword(password) && (
                <small style={{ color: 'red' }}>
                  密碼需為 8 到 20 碼，且同時包含英文與數字
                </small>
              )}
            </>
          )}

          <div className="pb-3" />

          <div className="mb-3">
            <div className="d-flex gap-2">
              <div className="form-floating w-50">
                <select
                  id="birthYear"
                  className="form-select form-select_gray"
                  value={birthYear}
                  onChange={(event) => setBirthYear(Number(event.target.value))}
                  required
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year} 年
                    </option>
                  ))}
                </select>
                <label htmlFor="birthYear">生日 *</label>
              </div>

              <select
                className="form-select form-select_gray w-25"
                value={birthMonth}
                onChange={(event) => setBirthMonth(Number(event.target.value))}
                required
              >
                {months.map((month) => (
                  <option key={month} value={month}>
                    {month} 月
                  </option>
                ))}
              </select>

              <select
                className="form-select form-select_gray w-25"
                value={birthDay}
                onChange={(event) => setBirthDay(Number(event.target.value))}
                required
              >
                {days.map((day) => (
                  <option key={day} value={day}>
                    {day} 日
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-floating mb-3">
            <select
              className="form-select form-select_gray"
              value={gender}
              onChange={(event) => setGender(event.target.value)}
              required
            >
              <option value="">請選擇性別</option>
              <option value="1">男性</option>
              <option value="2">女性</option>
              <option value="3">不方便透露</option>
            </select>
            <label>性別 *</label>
          </div>

          {hasFranchise && isLineMode && (
            <div className="mb-3">
              <label className="form-label d-block">註冊方式 *</label>
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="storeMode"
                    id="storeModeOffline"
                    checked={storeMode === 'offline'}
                    onChange={() => setStoreMode('offline')}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="storeModeOffline"
                  >
                    註冊店櫃
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="storeMode"
                    id="storeModeFranchise"
                    checked={storeMode === 'franchise'}
                    onChange={() => setStoreMode('franchise')}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="storeModeFranchise"
                  >
                    加盟店
                  </label>
                </div>
              </div>
            </div>
          )}

          {shouldShowOfflineStoreSelect ? (
            <div className="form-floating mb-3">
              <select
                className="form-select form-select_gray"
                value={selectedStoreId}
                onChange={(event) => setSelectedStoreId(event.target.value)}
                disabled={loadingStores || stores.length === 0}
                required
              >
                {stores.length === 0 ? (
                  <option value="">
                    {loadingStores ? '讀取中...' : '目前沒有可用店櫃'}
                  </option>
                ) : (
                  <>
                    <option value="">請選擇註冊店櫃</option>
                    {stores.map((store) => (
                      <option
                        key={store.OfflineStoreID}
                        value={store.OfflineStoreID}
                      >
                        {store.OfflineStoreName}
                      </option>
                    ))}
                  </>
                )}
              </select>
              <label>註冊店櫃 *</label>
            </div>
          ) : null}
          {isLineMode && usingFranchise ? (
            <div className="form-floating mb-3">
              <select
                className="form-select form-select_gray"
                value={selectedPartnerStoreId}
                onChange={(event) =>
                  setSelectedPartnerStoreId(event.target.value)
                }
                disabled={loadingPartnerStores || partnerStores.length === 0}
                required
              >
                {partnerStores.length === 0 ? (
                  <option value="">
                    {loadingPartnerStores ? '載入中...' : '目前沒有可選加盟店'}
                  </option>
                ) : (
                  partnerStores.map((store) => (
                    <option
                      key={String(store.PartnerStoreID)}
                      value={String(store.PartnerStoreID)}
                    >
                      {store.PartnerStoreName}
                    </option>
                  ))
                )}
              </select>
              <label>加盟店 *</label>
            </div>
          ) : null}

          {shouldShowReferrer && (
            <div className="form-floating mb-3">
              <select
                className="form-select form-select_gray"
                value={selectedReferrerId}
                onChange={(event) => setSelectedReferrerId(event.target.value)}
                disabled={loadingReferrers || referrers.length === 0}
                required
              >
                {referrers.length === 0 ? (
                  <option value="">
                    {loadingReferrers ? '讀取中...' : '查無推薦人'}
                  </option>
                ) : (
                  referrers.map((item) => (
                    <option key={String(item.EmpID)} value={String(item.EmpID)}>
                      {formatReferrerLabel(item)}
                    </option>
                  ))
                )}
              </select>
              <label>推薦人 *</label>
            </div>
          )}

          <button
            className="btn btn-primary w-100 text-uppercase"
            disabled={isStepOneDisabled}
            onClick={handleRequestOtp}
            type="button"
          >
            {loading ? '處理中...' : '取得驗證碼'}
          </button>
        </form>
      )}

      {step === 2 && (
        <form
          onSubmit={(event) => event.preventDefault()}
          className="needs-validation"
        >
          <div className="form-floating mb-3">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-control form-control_gray"
              placeholder="驗證碼 *"
              value={otp}
              onChange={(event) => setOtp(normalizeOtp(event.target.value))}
              maxLength={OTP_LENGTH}
              autoComplete="one-time-code"
              required
            />
            <label>驗證碼 *</label>
          </div>

          <div className="d-flex align-items-center mb-3 pb-2">
            <button
              className="btn btn-link p-0"
              type="button"
              disabled={cooldown > 0 || loading}
              onClick={handleResend}
            >
              重新發送{cooldown > 0 ? ` (${cooldown})` : ''}
            </button>
          </div>

          <button
            className="btn btn-primary w-100 text-uppercase"
            disabled={loading || otp.length !== OTP_LENGTH}
            onClick={handleVerifyAndCreate}
            type="button"
          >
            {loading ? '處理中...' : '完成註冊'}
          </button>
        </form>
      )}

      {step === 3 && (
        <div className="text-center py-4">
          <h5 className="mb-3">註冊完成</h5>
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={() => router.push('/')}
          >
            回到首頁
          </button>
          <p className="text-secondary mt-3">
            如果需要補充資料，也可以在這裡繼續完成。
          </p>
          <div className="d-flex justify-content-center gap-2 mt-2">
            <EditAccount onSaved={() => router.push('/')} />
          </div>
        </div>
      )}
    </div>
  )
}
